import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Activity, Leaf, Heart, Sparkles } from 'lucide-react';
import { thematicContent } from '../data/thematicContent';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';
import FAQ from '../components/FAQ';
import { faqMap } from '../data/faqContent';
import SEO from '../components/SEO';

const EquilibreEmotionnel = () => {
  const pageData = thematicContent.equilibreEmotionnel;

  return (
    <div style={{ marginTop: '100px' }}>
      <SEO
        title={pageData.meta.title}
        description={pageData.meta.description}
        keywords={pageData.meta.keywords}
        url="https://nutricentreriviera.ch/equilibre-emotionnel"
      />
      {/* Hero Section */}
      <section style={{
        background: 'var(--gradient-hero)',
        padding: '4rem 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            {pageData.h1}
          </h1>
          <p className="heading-subtitle" style={{ maxWidth: '900px', margin: '0 auto 2rem' }}>
            {pageData.intro}
          </p>
          <a href="https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid" target="_blank" rel="noopener noreferrer" className="btn-primary">
            Prendre rendez-vous
            <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {/* Main Content */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          {/* Section 1 */}
          <div style={{ marginBottom: '4rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
              {pageData.sections[0].title}
            </h2>
            <p className="body-large">
              {pageData.sections[0].content}
            </p>
          </div>

          {/* Section 2 - List */}
          <div style={{ marginBottom: '4rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
              {pageData.sections[1].title}
            </h2>
            <div style={{
              background: 'var(--bg-page)',
              borderRadius: '1rem',
              padding: '2rem',
              border: '1px solid var(--border-light)'
            }}>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                {pageData.sections[1].list.map((item, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <CheckCircle2 size={24} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.2rem'
                    }} />
                    <span className="body-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Section 3 - Subsections */}
          <div style={{ marginBottom: '4rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
              {pageData.sections[2].title}
            </h2>
            <div style={{
              display: 'grid',
              gap: '2rem'
            }}>
              {pageData.sections[2].subsections.map((sub, index) => (
                <div key={index} style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '2rem',
                  border: '1px solid var(--border-light)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <h3 className="heading-3" style={{ marginBottom: '0.75rem', color: 'var(--accent-primary)' }}>
                    {sub.subtitle}
                  </h3>
                  <p className="body-medium">
                    {sub.text}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4 - Pillars */}
          <div style={{ marginBottom: '4rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
              {pageData.sections[3].title}
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem'
            }}>
              {pageData.sections[3].pillars.map((pillar, index) => {
                const icons = [Activity, Leaf, Heart, Sparkles];
                const Icon = icons[index % icons.length];
                return (
                  <div key={index} style={{
                    background: 'white',
                    borderRadius: '1rem',
                    padding: '2rem',
                    border: '1px solid var(--border-light)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{
                      width: '3rem',
                      height: '3rem',
                      borderRadius: '0.75rem',
                      background: 'var(--accent-primary-light)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1rem'
                    }}>
                      <Icon size={24} style={{ color: 'var(--accent-primary)' }} />
                    </div>
                    <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                      {pillar.name}
                    </h3>
                    <p className="body-medium">
                      {pillar.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section 5 - Conditions */}
          <div style={{ marginBottom: '4rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
              {pageData.sections[4].title}
            </h2>
            <div style={{
              background: 'var(--accent-primary-light)',
              borderRadius: '1rem',
              padding: '2rem',
              border: '1px solid var(--accent-primary)'
            }}>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '1rem'
              }}>
                {pageData.sections[4].conditions.map((condition, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem'
                  }}>
                    <CheckCircle2 size={20} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.25rem'
                    }} />
                    <span className="body-medium">{condition}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FAQ items={faqMap['equilibre-emotionnel']} />

      <RelatedPages items={relatedPagesMap['equilibre-emotionnel']} />

      {/* CTA Section */}
      <section style={{
        background: 'var(--accent-primary)',
        color: 'white',
        padding: '4rem 0'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-2" style={{ color: 'white', marginBottom: '1rem' }}>
            {pageData.cta.title}
          </h2>
          <p className="body-large" style={{ 
            color: 'white', 
            opacity: 0.95, 
            maxWidth: '700px', 
            margin: '0 auto 2rem' 
          }}>
            {pageData.cta.text}
          </p>
          <a
            href="https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ 
              background: 'white', 
              color: 'var(--accent-primary)',
              textDecoration: 'none'
            }}
          >
            {pageData.cta.button}
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default EquilibreEmotionnel;
